import { lazy, Suspense } from 'react';

import { formattedValueToString, getValueFormat, store, type SelectableValue } from '@grafana/data';
import { t } from '@grafana/i18n';
import {
  type SceneComponentProps,
  sceneGraph,
  SceneObjectBase,
  type SceneObjectRef,
  type VizPanel,
} from '@grafana/scenes';
import { type Dashboard } from '@grafana/schema';
import { Spinner } from '@grafana/ui';
import { createErrorNotification, createSuccessNotification } from 'app/core/copy/appNotification';
import { notifyApp } from 'app/core/reducers/appNotification';
import { getTrackingSource, shareDashboardType } from 'app/features/dashboard/components/ShareModal/utils';
import { getDashboardSnapshotSrv, type SnapshotSharingOptions } from 'app/features/dashboard/services/SnapshotSrv';
import { dispatch } from 'app/store/store';

import { type Spec as DashboardV2Spec } from '../../../../../packages/grafana-schema/src/schema/dashboard/v2';
import { type DashboardScene } from '../scene/DashboardScene';
import { transformSceneToSaveModel, trimDashboardForSnapshot } from '../serialization/transformSceneToSaveModel';
import {
  transformSceneToSaveModelSchemaV2,
  trimDashboardForSnapshot as trimDashboardForSnapshotV2,
} from '../serialization/transformSceneToSaveModelSchemaV2';
import { DashboardInteractions } from '../utils/interactions';

import { getExpireOptions } from './snapshotOptions';
import { type SceneShareTabState, type ShareView } from './types';

const ShareSnapshotTabRenderer = lazy(() =>
  import('./ShareRenderers').then((m) => ({ default: m.ShareSnapshotTabRenderer }))
);

function LazyShareSnapshotTabRenderer(props: SceneComponentProps<ShareSnapshotTab>) {
  return (
    <Suspense fallback={<Spinner />}>
      <ShareSnapshotTabRenderer {...props} />
    </Suspense>
  );
}

// A snapshot embeds every panel's query results, so a dashboard that repeats panels over many
// variable values can serialize to a body no request can deliver. This mirrors the apiserver's
// MaxRequestBodyBytes, the lower of the two ceilings a create request can hit, so the check is
// valid whether the request goes to /apis or to the legacy /api endpoint. A reverse proxy in
// front of Grafana may also cap body size and reject the request before it reaches Grafana at
// all, leaving no response we can turn into a useful message.
const MAX_SNAPSHOT_PAYLOAD_BYTES = 16 * 1024 * 1024;

// JSON.stringify().length counts UTF-16 code units, which undercounts every non-ASCII series
// name or label value in the embedded data, so measure the encoded length actually sent.
export function getSnapshotPayloadSizeBytes(payload: object): number {
  return new Blob([JSON.stringify(payload)]).size;
}

const SIZE_DECIMALS = 1;

// IEC units to match the 1024-based limit above.
function formatBytes(bytes: number): string {
  return formattedValueToString(getValueFormat('bytes')(bytes, SIZE_DECIMALS));
}

// Rounds up to the precision we render, so a payload only slightly over the limit is never
// reported as equal to it ("16.0 MiB, over the 16.0 MiB limit").
export function formatSnapshotSize(bytes: number): string {
  const unit = 1024 ** Math.floor(Math.log2(Math.max(bytes, 1)) / 10);
  const step = unit / 10 ** SIZE_DECIMALS;
  return formatBytes(Math.ceil(bytes / step) * step);
}

const SNAPSHOT_SHARE_CONFIGURATION = 'grafana.dashboard.snapshot.shareConfiguration';

type SnapshotShareConfiguration = {
  expirationTime: number;
};

// Returns the snapshot share configuration persisted in local storage, if any
function getSnapshotShareConfiguration(): SnapshotShareConfiguration | undefined {
  if (store.exists(SNAPSHOT_SHARE_CONFIGURATION)) {
    return store.getObject<SnapshotShareConfiguration>(SNAPSHOT_SHARE_CONFIGURATION);
  }
  return undefined;
}

function updateSnapshotShareConfiguration(config: SnapshotShareConfiguration) {
  store.setObject(SNAPSHOT_SHARE_CONFIGURATION, config);
}

const getDefaultExpireOption = () => {
  const savedConfiguration = getSnapshotShareConfiguration();
  if (savedConfiguration) {
    const savedOption = getExpireOptions().find((o) => o.value === savedConfiguration.expirationTime);
    if (savedOption) {
      return savedOption;
    }
  }
  return getExpireOptions()[2];
};

export interface ShareSnapshotTabState extends SceneShareTabState {
  dashboardRef: SceneObjectRef<DashboardScene>;
  panelRef?: SceneObjectRef<VizPanel>;
  snapshotName: string;
  selectedExpireOption: SelectableValue<number>;
  snapshotSharingOptions?: SnapshotSharingOptions;
}

// this is a hacky way to pass the uid with the dashboard to the backend so the dashboard can be found
// and snapshot can be created
interface DashboardV2SpecWithUid extends DashboardV2Spec {
  uid?: string;
}

export class ShareSnapshotTab extends SceneObjectBase<ShareSnapshotTabState> implements ShareView {
  public tabId = shareDashboardType.snapshot;
  static Component = LazyShareSnapshotTabRenderer;

  // Overridable so the size guard can be exercised without building a payload this large
  protected maxPayloadSizeBytes = MAX_SNAPSHOT_PAYLOAD_BYTES;

  public constructor(
    state: Omit<ShareSnapshotTabState, 'snapshotName' | 'selectedExpireOption' | 'snapshotSharingOptions'>
  ) {
    super({
      ...state,
      snapshotName: state.dashboardRef.resolve().state.title,
      selectedExpireOption: getDefaultExpireOption(),
    });

    this.addActivationHandler(() => {
      this._onActivate();
    });
  }

  private _onActivate() {
    getDashboardSnapshotSrv()
      .getSharingOptions()
      .then((shareOptions) => {
        if (this.isActive) {
          this.setState({
            snapshotSharingOptions: shareOptions,
          });
        }
      });
  }

  public getTabLabel() {
    return t('share-modal.tab-title.snapshot', 'Snapshot');
  }

  public onSnasphotNameChange = (snapshotName: string) => {
    this.setState({ snapshotName });
  };

  public onExpireChange = (option: number) => {
    this.setState({
      selectedExpireOption: getExpireOptions().find((o) => o.value === option),
    });
    updateSnapshotShareConfiguration({ expirationTime: option });
  };

  private prepareSnapshot() {
    const timeRange = sceneGraph.getTimeRange(this);
    const { dashboardRef, panelRef } = this.state;

    let saveModel: Dashboard | DashboardV2SpecWithUid;

    const apiVersion = dashboardRef.resolve().serializer.apiVersion;

    const isV2Dashboard = apiVersion?.startsWith('dashboard.grafana.app/v2') ?? false;

    if (isV2Dashboard) {
      saveModel = transformSceneToSaveModelSchemaV2(dashboardRef.resolve(), true);
      saveModel.uid = dashboardRef.resolve().serializer.getK8SMetadata()?.name;
      return trimDashboardForSnapshotV2(
        this.state.snapshotName.trim() || '',
        timeRange.state.value,
        saveModel,
        panelRef?.resolve()
      );
    }

    saveModel = transformSceneToSaveModel(dashboardRef.resolve(), true);

    return trimDashboardForSnapshot(
      this.state.snapshotName.trim() || '',
      timeRange.state.value,
      saveModel,
      panelRef?.resolve()
    );
  }

  public onSnapshotCreate = async (external = false) => {
    const { selectedExpireOption } = this.state;
    const snapshot = this.prepareSnapshot();
    const cmdData = {
      dashboard: snapshot,
      name: snapshot.title,
      expires: selectedExpireOption?.value,
      external,
    };

    const payloadSizeBytes = getSnapshotPayloadSizeBytes(cmdData);

    try {
      if (payloadSizeBytes > this.maxPayloadSizeBytes) {
        const message = t(
          'snapshot.share.too-large-body',
          'This snapshot is {{size}}, over the {{limit}} limit. Snapshot a single panel, shorten the time range, or select fewer template variable values, then try again.',
          {
            size: formatSnapshotSize(payloadSizeBytes),
            limit: formatBytes(this.maxPayloadSizeBytes),
          }
        );

        dispatch(
          notifyApp(
            createErrorNotification(t('snapshot.share.too-large-title', 'Snapshot is too large to publish'), message)
          )
        );

        throw new Error(message);
      }

      const response = await getDashboardSnapshotSrv().create(cmdData);
      dispatch(
        notifyApp(createSuccessNotification(t('snapshot.share.success-creation', 'Your snapshot has been created')))
      );
      return response;
    } finally {
      if (external) {
        DashboardInteractions.publishSnapshotClicked({
          expires: cmdData.expires,
          shareResource: getTrackingSource(this.state.panelRef),
        });
      } else {
        DashboardInteractions.publishSnapshotLocalClicked({
          expires: cmdData.expires,
          shareResource: getTrackingSource(this.state.panelRef),
        });
      }
    }
  };

  public onSnapshotDelete = async (key: string) => {
    const response = await getDashboardSnapshotSrv().deleteSnapshot(key);
    dispatch(
      notifyApp(createSuccessNotification(t('snapshot.share.success-delete', 'Your snapshot has been deleted')))
    );
    return response;
  };
}
